import React, { useContext, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import styled from 'styled-components/native';
import { DateTime } from 'luxon';
import moment from 'moment';
import FileDownload from 'js-file-download';

import DataState from 'app/src/components/DataState';
import AuthContext from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import minimalTable from 'app/src/elements/minimalTable';
import Modal from 'app/src/elements/Modal';
import { Text } from 'app/src/styles';
import { elGreen, elRed } from 'app/src/styles/colors';
import { rest } from 'app/src/utils/rest';
import { Link } from 'app/src/utils/routing';
import { currency } from 'app/src/utils/stringHelpers';
import { dateShort } from 'app/src/utils/timeFormatter';

import EditInvoice from './edit';
import {
  saleAssetColumns,
  creditMemoColumns,
  feeColumns,
  Section,
} from './new';
import PrintInvoice from './PrintInvoice';
import EmailInvoice from './EmailInvoice';
import {
  INVOICE,
  REMOVE_SALE_ASSET_FROM_INVOICE,
  ADD_SALE_ASSETS_TO_INVOICE,
  ADD_FEES_TO_INVOICE,
  ADD_CREDIT_MEMOS_TO_INVOICE,
} from './queries';

import CopyDirectLink from '../Txns/CopyDirectLink';
import { DESTROY_TXN, DESTROY_TXN_LINE } from '../Txns/queries';

const Subtitle = styled(Text)`
  font-weight: bold
  font-size: 18px
  margin-bottom: 6px;
`;

const columns = [
  {
    key: 'stockNumber',
    label: 'Stock #',
    value: ({ entry }) => entry.saleAsset.asset.stockNumber,
    content: ({ entry, value }) => (
      <Link to={entry.saleAsset.asset.webPath} target="_blank">
        <Text style={{ fontSize: 14 }}>{value}</Text>
      </Link>
    )
  },
  {
    key: 'lotNumber',
    label: 'Lot #',
    value: ({ entry }) => entry.saleAsset.lotNumber,
  },
  {
    key: 'saleOn',
    label: 'Sale Date',
    value: ({ entry }) => dateShort(entry.saleAsset.sale?.saleOn),
  },
  {
    key: 'soldAmount',
    label: 'Sale Price',
    content: ({ value }) => currency(value),
  },
  {
    key: 'fees',
    label: 'Fees',
    sort: 'prevent',
    width: 200,
    content: ({ value }) => (
      <View>
        {value.map(feeData => (
          <View key={feeData.fee.id}>
            <Text>
              {feeData.fee.description || feeData.fee.type}
              {' - '}
              {currency(feeData.amount)}
            </Text>
          </View>
        ))}
      </View>
    )
  },
  {
    key: 'totalAmount',
    label: 'Asset Total',
    content: ({ value }) => currency(value),
  },
  {
    key: 'actions',
    label: 'Actions',
    sort: 'prevent',
    content: ({ value }) => '',
  },
];

const appliedFeesColumns = [
  {
    key: 'stockNumber',
    label: 'Stock #',
    value: ({ entry }) => entry.asset.stockNumber || entry.asset.vin,
  },
  {
    key: 'creditLessDebit',
    label: 'Amount',
    content: ({ value }) => currency(value),
  },
  {
    key: 'type',
    label: 'Type',
    value: ({ entry }) => entry.fee.type,
  },
  {
    key: 'description',
    label: 'Description',
    value: ({ entry }) => entry.fee.description,
  },
  {
    key: 'actions',
    label: 'Actions',
    sort: 'prevent',
    content: ({ value }) => '',
  },
];

const appliedCreditMemoColumns = [
  {
    key: 'bankDate',
    label: 'Created Date',
    content: ({ entry }) => dateShort(entry.txn.bankDate)
  },
  {
    key: 'debitLessCredit',
    label: 'Credit Amount',
    content: ({ value }) => currency(value),
  },
]

const correspondenceColumns = [
  {
    key: 'createdAt',
    label: 'Sent At',
    content: ({ value }) => moment(value).format('MM-DD-YYYY h:mm:ss a')
  },
  {
    key: 'recipientList',
    label: 'Received By',
    content: ({ value }) => {
      let parsed = JSON.parse(value);
      return (
        <View>
          <View style={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
            <Text>To:</Text>
            <Text>{parsed['email_to']}</Text>
          </View>
          <View style={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
            <Text>CC:</Text>
            <Text>{parsed['cc_emails_to']}</Text>
          </View>
        </View>
      )
    }
  },
  {
    key: 'attachment',
    label: 'Invoice',
    content: ({ entry }) => {
      return (
        <Link to={entry.attachment.url} target="_blank">
          <Text>Download</Text>
        </Link>
      )
    }
  }
];

const Invoice = ({ id }) => {
  const { credentials, hasPolicy } = useContext(AuthContext);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmailInvoiceModal, setShowEmailInvoiceModal] = useState(false);
  const [showAddAssets, setShowAddAssets] = useState(false);
  const [showAddFees, setShowAddFees] = useState(false);
  const [showAddCreditMemos, setShowAddCreditMemos] = useState(false);
  const [formStatus, setFormStatus] = useState();
  const [saleAssetAmounts, setSaleAssetAmounts] = useState([]);
  const [feeAmounts, setFeeAmounts] = useState([]);
  const [showSellFees, setShowSellFees] = useState(false);
  const [showOtherAssetFees, setShowOtherAssetFees] = useState(false);
  const [creditMemoAmounts, setCreditMemoAmounts] = useState([]);
  const [emailStatus, setEmailStatus] = useState();

  const { data, refetch } = useQuery(INVOICE, { variables: { id } });

  const [removeSaleAssetFromInvoice] = useMutation(
    REMOVE_SALE_ASSET_FROM_INVOICE,
    { onCompleted: refetch }
  );

  const [destroyTxn] = useMutation(
    DESTROY_TXN,
    { onCompleted: () => window.location.href = '/accounting/invoices' }
  );

  const [destroyTxnLine] = useMutation(
    DESTROY_TXN_LINE,
    { onCompleted: refetch }
  );

  const [addSaleAssetsToInvoice] = useMutation(ADD_SALE_ASSETS_TO_INVOICE, {
    onCompleted: () => {
      refetch();
      setShowAddAssets(false);
      setFormStatus('');
    }
  });

  const [addFeesToInvoice] = useMutation(ADD_FEES_TO_INVOICE, {
    onCompleted: () => {
      refetch();
      setShowAddFees(false);
      setFormStatus('');
    }
  });

  const [addCreditMemosToInvoice] = useMutation(ADD_CREDIT_MEMOS_TO_INVOICE, {
    onCompleted: () => {
      refetch();
      setShowAddCreditMemos(false);
      setFormStatus('');
    }
  });

  if (!data) { return <DataState.Loading /> };

  const handleRemove = entry => {
    const confirmation =
      `Remove ${entry.saleAsset.asset.stockNumber} from this invoice?`;

    if (window.confirm(confirmation)) {
      removeSaleAssetFromInvoice({
        variables: { txnId: id, saleAssetId: entry.saleAsset.id }
      });
    }
  };

  const handleRemoveFee = entry => {
    const confirmation =
      `Remove ${entry.fee.type} Fee for ${entry.asset.stockNumber} ` +
      'from this invoice?';

    if (window.confirm(confirmation)) {
     destroyTxnLine({ variables: { id: entry.id } });
    }
  };

  const handleDelete = () => {
    if (window.confirm('Really delete invoice?')) {
      destroyTxn({ variables: { id } });
    }
  }

  const handleAddSubmit = () => {
    setFormStatus('submitting');

    addSaleAssetsToInvoice({
      variables: {
        txnId: id,
        saleAssetIds: saleAssetAmounts.map(saleAsset => saleAsset.id),
      }
    });
  };

  const handleAddFeesSubmit = () => {
    setFormStatus('submitting');

    addFeesToInvoice({
      variables: {
        txnId: id,
        feeIds: feeAmounts.map(fee => fee.id),
      }
    });
  };

  const handleAddCreditsSubmit = () => {
    setFormStatus('submitting');

    addCreditMemosToInvoice({
      variables: {
        txnId: id,
        creditLineAmounts: creditMemoAmounts,
      }
    });
  };

  const downloadInvoice = () => {
    let path = `accounting/invoice/${data.txn.id}.pdf`;

    rest({
      path,
      options: { headers: { Accept: 'application/pdf' } },
      credentials,
    }).then(response => {
      const fullFilename = `Invoice-${data.txn.reference}.pdf`;
      response.blob().then(blob => FileDownload(blob, fullFilename));
    });
  };

  const canEdit =
    data.txn.bankDate > DateTime.now().minus({ months: 3 }).toISODate() &&
    hasPolicy({ type: 'Txn', action: 'update' });

  if (canEdit) {
    columns[columns.length - 1].content = ({ entry }) => (
      <Pressable onPress={() => handleRemove(entry)}>
        <Text>Remove</Text>
      </Pressable>
    );

    appliedFeesColumns[appliedFeesColumns.length - 1].content = ({ entry }) => (
      <Pressable onPress={() => handleRemoveFee(entry)}>
        <Text>Remove</Text>
      </Pressable>
    );
  } else {
    columns[columns.length - 1].content = () => '';
    appliedFeesColumns[appliedFeesColumns.length - 1].content = () => '';
  }

  const fees = data.txn.txnLines.filter(txnLine => (
    'Fee Invoice' === data.txn.type ?
      !!txnLine.fee : !!(txnLine.fee && !txnLine.saleAsset)
  ));

  const creditMemos = data.txn.txnLines.slice(1).filter(txnLine => (
    txnLine.offsetId && 'ar' == txnLine.account.special
  ));

  let detailContent = (
    <>
      <PrintInvoice txn={data.txn} />
      <View style={{marginTop: '20px'}}>
        <Subtitle>Previous Emails</Subtitle>
        <DataTable.Simple
          data={data.txn.correspondence}
          columns={correspondenceColumns}
          component={minimalTable}
          pageLength="All"
        />
      </View>
    </>
  );

  if (showAddAssets && data.txn.invoiceAddableSaleAssets) {
    detailContent = (
      <Section style={{ marginTop: 20 }}>
        <Subtitle>Assets Ready to Invoice for Sale</Subtitle>

        <DataTable.Simple
          data={data.txn.invoiceAddableSaleAssets}
          columns={
            saleAssetColumns({ saleAssetAmounts, setSaleAssetAmounts })
          }
          component={minimalTable}
          pageLength="All"
        />

        {formStatus ? (
          <Text>{formStatus}</Text>
        ) : (
          <ButtonGroup>
            <Button label="Add to Invoice" onPress={handleAddSubmit} />
          </ButtonGroup>
        )}
      </Section>
    );
  } else if (showAddFees && data.txn.invoiceAddableFees) {
    const assetIds = data.txn.txnLines.map(txnLine => txnLine.asset?.id);

    const feesData = data.txn.invoiceAddableFees.filter(fee => {
      if ('0.0' === fee.amount) { return false; }
      if ('Sell' === fee.type && !showSellFees) { return false; }
      if (!assetIds.includes(fee.asset.id) && !showOtherAssetFees) {
        return false;
      }
      return true;
    });

    detailContent = (
      <Section style={{ marginTop: 20 }}>
        <Subtitle>Fees Not Invoiced</Subtitle>

        <Pressable onPress={() => setShowSellFees(!showSellFees)}>
          <Text>[{showSellFees ? 'Hide' : 'Show'} Sell Fees]</Text>
        </Pressable>

        <Pressable
          onPress={() => setShowOtherAssetFees(!showOtherAssetFees)}
        >
          <Text>
            [{showOtherAssetFees ? 'Hide' : 'Show'} Fees on Other Assets]
          </Text>
        </Pressable>

        <DataTable.Simple
          data={feesData}
          columns={feeColumns({ feeAmounts, setFeeAmounts })}
          component={minimalTable}
          pageLength="All"
        />

        {formStatus ? (
          <Text>{formStatus}</Text>
        ) : (
          <ButtonGroup>
            <Button label="Add to Invoice" onPress={handleAddFeesSubmit} />
          </ButtonGroup>
        )}
      </Section>
    );
  } else if (showAddCreditMemos && data.txn.invoiceAddableCreditMemoLines) {
    detailContent = (
      <Section style={{ marginTop: 20 }}>
        <Subtitle>Available Customer Credits</Subtitle>

        <DataTable.Simple
          data={data.txn.invoiceAddableCreditMemoLines}
          columns={
            creditMemoColumns({
              creditMemoAmounts,
              setCreditMemoAmounts,
              maxCreditMemo: parseFloat(data.txn.amount),
            })
          }
          component={minimalTable}
          pageLength="All"
        />

        {formStatus ? (
          <Text>{formStatus}</Text>
        ) : (
          <ButtonGroup>
            <Button
              label="Credit to Invoice"
              onPress={handleAddCreditsSubmit}
            />
          </ButtonGroup>
        )}
      </Section>
    );
  }

  return (
    <View style={{ overflowX: 'scroll', minWidth: 1000 }}>
      <View style={{ flexDirection: 'row', gap: 20, marginBottom: 10 }}>
        {canEdit && (
          <Pressable onPress={() => setShowEditModal(true)}>
            <Text>Edit Invoice</Text>
          </Pressable>
        )}

        <Pressable onPress={() => setShowEmailInvoiceModal(true)}>
          <Text>Email Invoice</Text>
        </Pressable>

        <Pressable onPress={downloadInvoice}>
          <Text>Download Invoice</Text>
        </Pressable>

        {canEdit && 'Invoice' === data.txn.type && (
          <Pressable onPress={() => setShowAddAssets(true)}>
            <Text>Add Assets</Text>
          </Pressable>
        )}

        {canEdit && 'Fee Invoice' === data.txn.type && (
          <Pressable onPress={() => setShowAddFees(true)}>
            <Text>Add Fees</Text>
          </Pressable>
        )}

        {canEdit && data.txn.invoiceAddableCreditMemoLines.length > 0 && (
          <Pressable
            onPress={() => {
              setShowAddAssets(false);
              setShowAddCreditMemos(true);
            }}
          >
            <Text>Add Credits</Text>
          </Pressable>
        )}

        {canEdit && (
          <Pressable onPress={handleDelete}>
            <Text>Delete Invoice</Text>
          </Pressable>
        )}

        <CopyDirectLink developerOnly txn={data.txn} />

        <Text
          style={{
            color: emailStatus === 'Emailing Invoice...' ? elRed : elGreen
          }}
        >
          {emailStatus}
        </Text>
      </View>

      {data.txn.offsettingTxns.length > 0 && (
        <View style={{ marginBottom: 20 }}>
          <Subtitle>Payments</Subtitle>

          {data.txn.offsettingTxns.map(paymentTxn => (
            <Link
              key={paymentTxn.id}
              to={`/accounting/payments/${paymentTxn.id}`}
            >
              <Text>
                {dateShort(paymentTxn.bankDate)} {currency(paymentTxn.amount)}
              </Text>
            </Link>
          ))}
        </View>
      )}

      <Subtitle>Assets Included in Invoice</Subtitle>

      {'Invoice' === data.txn.type && (
        <DataTable.Simple
          data={data.txn.invoiceSaleAssets}
          columns={columns}
          component={minimalTable}
          pageLength="All"
          htmlTable
        />
      )}

      {('Fee Invoice' === data.txn.type || fees.length > 0) && (
        <DataTable.Simple
          data={fees}
          columns={appliedFeesColumns}
          component={minimalTable}
          pageLength="All"
          htmlTable
        />
      )}

      {creditMemos.length > 0 && (
        <>
          <Subtitle style={{ marginTop: 20 }}>Customer Credits</Subtitle>

          <DataTable.Simple
            data={creditMemos}
            columns={appliedCreditMemoColumns}
            component={minimalTable}
            pageLength="All"
            htmlTable
          />
        </>
      )}

      {detailContent}

      {showEditModal && (
        <Modal
          showModal
          setShowModal={setShowEditModal}
          modalType="Edit Invoice"
        >
          <EditInvoice
            txn={data.txn}
            setShowModal={setShowEditModal}
            refetch={refetch}
          />
        </Modal>
      )}

      {showEmailInvoiceModal && (
        <Modal
          showModal
          setShowModal={setShowEmailInvoiceModal}
          modalType="Email Invoice"
        >
          <EmailInvoice
            txn={data.txn}
            setShowModal={setShowEmailInvoiceModal}
            refetch={refetch}
            setEmailStatus={setEmailStatus}
          />
        </Modal>
      )}
    </View>
  );
};

export default Invoice;
